import { extractFatturaXml, gestifyItemPath, type InvoiceDirection } from '@k-management/shared';
import { GestifyBrowserError, type useGestifyBrowser } from '../../lib/gestify-browser.js';

type Gestify = ReturnType<typeof useGestifyBrowser>;
type File = { contentType: string; bytes: Uint8Array };

/**
 * What happened on each try, by kind only (never a value), said if nothing
 * works; and the links of storages the browser may not read, for the server.
 */
type Notes = string[] & { serverLinks?: string[] };

/** Bytes compressed by Gestify (gzip, deflate), opened by the browser; others as they are. */
async function inflate(bytes: Uint8Array, notes: Notes): Promise<Uint8Array> {
  const format: CompressionFormat | null =
    bytes[0] === 0x1f && bytes[1] === 0x8b
      ? 'gzip'
      : bytes[0] === 0x78 && [0x01, 0x5e, 0x9c, 0xda].includes(bytes[1] ?? 0)
        ? 'deflate'
        : null;
  if (!format) return bytes;
  try {
    const stream = new Blob([bytes as BlobPart])
      .stream()
      .pipeThrough(new DecompressionStream(format));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    notes.push(`file compresso (${format}) non apribile`);
    return bytes;
  }
}

/** Every string in a JSON answer, with the path of its field (for the notes: names only). */
function strings(value: unknown, path = ''): [string, string][] {
  if (typeof value === 'string') return [[path || 'valore', value]];
  if (Array.isArray(value))
    return value.flatMap((item, index) => strings(item, `${path}[${index}]`));
  if (value !== null && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) =>
      strings(item, path ? `${path}.${key}` : key),
    );
  }
  return [];
}

/** A link of an answer: on Gestify with the session, elsewhere (a storage) without anything. */
async function follow(gestify: Gestify, link: string, notes: Notes): Promise<File | null> {
  try {
    const url = new URL(link, 'https://api.gestify.it');
    if (url.hostname.endsWith('gestify.it')) {
      return await gestify.download(`${url.pathname}${url.search}`);
    }
    const response = await fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer' });
    if (!response.ok) {
      notes.push(`link esterno con errore ${response.status}`);
      return null;
    }
    return {
      contentType: response.headers.get('content-type') ?? '',
      bytes: new Uint8Array(await response.arrayBuffer()),
    };
  } catch {
    // The storage does not let pages of other sites read it: the server will.
    (notes.serverLinks ??= []).push(link);
    return null;
  }
}

/** The XML of an electronic invoice in a file of Gestify, whatever the way it comes. */
async function xmlOf(
  gestify: Gestify,
  file: File,
  notes: Notes,
  depth = 0,
): Promise<string | null> {
  const bytes = await inflate(file.bytes, notes);
  if (!file.contentType.includes('json')) {
    const xml = extractFatturaXml(bytes);
    if (!xml) notes.push(`file ${file.contentType || 'senza tipo'} senza fattura elettronica`);
    return xml;
  }
  let answer: unknown;
  try {
    answer = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    notes.push('risposta JSON non leggibile');
    return null;
  }
  const found = strings(answer);
  for (const [, value] of found) {
    // The XML as text…
    const direct = extractFatturaXml(new TextEncoder().encode(value));
    if (direct) return direct;
    // …in base64…
    if (/^[A-Za-z0-9+/=\s]{200,}$/.test(value)) {
      try {
        const binary = atob(value.replace(/\s/g, ''));
        const decoded = await inflate(
          Uint8Array.from(binary, (char) => char.charCodeAt(0)),
          notes,
        );
        const xml = extractFatturaXml(decoded);
        if (xml) return xml;
      } catch {
        // Not base64 after all.
      }
    }
  }
  // …or behind a link.
  if (depth < 2) {
    for (const [, value] of found) {
      if (!/^(https?:\/\/|\/api\/)/.test(value)) continue;
      const linked = await follow(gestify, value, notes);
      const xml = linked && (await xmlOf(gestify, linked, notes, depth + 1));
      if (xml) return xml;
    }
  }
  notes.push(
    `risposta JSON senza fattura elettronica (campi: ${found.map(([path]) => path).join(', ') || 'nessuno'})`,
  );
  return null;
}

function idOf(value: unknown): string | null {
  const id = value !== null && typeof value === 'object' ? (value as { id?: unknown }).id : value;
  return id === null || id === undefined || id === '' ? null : String(id);
}

/**
 * The electronic invoice of a document of Gestify, read by this browser
 * (07/10/2026). The answers of the API are not documented: the attachments of
 * the document are tried first (the official XML, then the XML), then the
 * download of the electronic invoices. What fails is told by kind and field
 * names only, never by content.
 */
export async function readInvoiceXml(
  gestify: Gestify,
  direction: InvoiceDirection,
  gestifyId: string,
  /** Downloads a public link through the server of K-Management, which keeps the invoice. */
  viaServer: (url: string) => Promise<string>,
): Promise<{ xml: string; stored: boolean }> {
  const notes: Notes = [];
  const item = await gestify.read(gestifyItemPath(direction, gestifyId));
  const attachments = await gestify
    .readAll(`/api/allegati/v1/documenti/${gestifyId}/list`)
    .catch(() => [] as Record<string, unknown>[]);
  const isXml = (attachment: Record<string, unknown>) =>
    /xml|p7m/i.test(String(attachment.file_estensione ?? attachment.file_nome ?? ''));
  const ranked = [
    ...attachments.filter((attachment) => attachment.fe_xml_ufficiale === true),
    ...attachments.filter((attachment) => attachment.fe_xml === true),
    ...attachments.filter(isXml),
  ];
  const ids = [
    ...new Set(
      [...ranked.map((attachment) => idOf(attachment.id)), idOf(item.xml_allegato_id)].filter(
        (id): id is string => id !== null,
      ),
    ),
  ];
  const paths = [
    ...ids.map((id) => `/api/allegati/v1/documenti/${gestifyId}/item/${id}/download`),
    `/api/documenti/fe/${direction === 'emessa' ? 'attive' : 'ricevute'}/v1/item/${gestifyId}/download/XML`,
  ];
  if (!ids.length) notes.push('nessun allegato XML nel documento');
  for (const path of paths) {
    try {
      const xml = await xmlOf(gestify, await gestify.download(path), notes);
      if (xml) return { xml, stored: false };
    } catch (error) {
      notes.push(error instanceof Error ? error.message.replace(/\.$/, '') : 'errore');
    }
  }
  for (const link of new Set(notes.serverLinks ?? [])) {
    try {
      return { xml: await viaServer(link), stored: true };
    } catch (error) {
      notes.push(
        `link scaricato dal server: ${error instanceof Error ? error.message.replace(/\.$/, '') : 'errore'}`,
      );
    }
  }
  throw new GestifyBrowserError(
    `Gestify non ha dato la fattura elettronica (${[...new Set(notes)].join('; ')}).`,
  );
}
